import { useCallback, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import type { PluginListenerHandle } from '@capacitor/core';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getToken } from 'firebase/messaging';
import { toast } from 'react-toastify';
import { db } from '../firebase/firebaseConfig';
import { getMessagingIfSupported } from '../firebase/firebaseMessaging';
import { useAuth } from '../hooks/useAuth';
import { Button } from './ui/button';

const PushNotificationManager = () => {
  const { user, userRole, studioId } = useAuth();
  const [showWebPrompt, setShowWebPrompt] = useState(false);
  const [webPermission, setWebPermission] = useState<NotificationPermission>('default');
  const [isRegisteringWeb, setIsRegisteringWeb] = useState(false);

  const saveToken = useCallback(async (token: string) => {
    if (!user?.uid || !token) return;
    const tokenRef = doc(db, 'users', user.uid, 'fcmTokens', token);
    await setDoc(
      tokenRef,
      {
        token,
        platform: Capacitor.getPlatform(),
        updatedAt: serverTimestamp(),
        studioId: studioId ?? null,
        role: userRole ?? null,
      },
      { merge: true }
    );
  }, [user?.uid, studioId, userRole]);

  useEffect(() => {
    if (!user || !Capacitor.isNativePlatform()) return;

    let cancelled = false;

    const register = async () => {
      try {
        const permission = await PushNotifications.checkPermissions();
        if (permission.receive !== 'granted') {
          const req = await PushNotifications.requestPermissions();
          if (req.receive !== 'granted') {
            toast.info('Notifications are disabled. You can enable them in Settings.');
            return;
          }
        }

        await PushNotifications.register();
      } catch (err) {
        if (!cancelled) {
          console.error('Push registration failed', err);
          toast.error('Unable to enable push notifications.');
        }
      }
    };

    let registrationHandle: PluginListenerHandle | null = null;
    let registrationErrorHandle: PluginListenerHandle | null = null;

    const attachListeners = async () => {
      registrationHandle = await PushNotifications.addListener(
        'registration',
        (token) => {
          if (!token?.value) return;
          saveToken(token.value).catch((err) => {
            console.error('Failed to save push token', err);
          });
        }
      );

      registrationErrorHandle = await PushNotifications.addListener(
        'registrationError',
        (error) => {
          console.error('Push registration error', error);
        }
      );
    };

    attachListeners();
    register();

    return () => {
      cancelled = true;
      registrationHandle?.remove();
      registrationErrorHandle?.remove();
    };
  }, [user, saveToken]);

  useEffect(() => {
    if (!user || Capacitor.isNativePlatform()) return;
    if (typeof window === 'undefined') return;
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;

    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;
    if (!isStandalone) return;

    setWebPermission(Notification.permission);
    if (Notification.permission === 'default') {
      setShowWebPrompt(true);
    }
  }, [user]);

  const registerWebPush = async () => {
    if (isRegisteringWeb) return;
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      toast.error('Missing VAPID key. Set REACT_APP_FIREBASE_VAPID_KEY and rebuild.');
      return;
    }

    setIsRegisteringWeb(true);
    try {
      const permission = await Notification.requestPermission();
      setWebPermission(permission);
      if (permission !== 'granted') {
        toast.info('Notifications are disabled. You can enable them in browser settings.');
        return;
      }

      const messaging = await getMessagingIfSupported();
      if (!messaging) {
        toast.error('Push notifications are not supported in this browser.');
        return;
      }

      const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      const token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: swReg,
      });

      if (!token) {
        toast.error('No notification token returned.');
        return;
      }

      await saveToken(token);
      toast.success('Notifications enabled.');
      setShowWebPrompt(false);
    } catch (err) {
      console.error('Web push registration failed', err);
      toast.error('Unable to enable web notifications.');
    } finally {
      setIsRegisteringWeb(false);
    }
  };

  if (!showWebPrompt || Capacitor.isNativePlatform()) return null;

  return (
    <div
      className="fixed bottom-2 left-4 right-4 z-40 rounded-2xl border border-[#2a2a2a] bg-[#121212]/95 p-4 text-white shadow-lg backdrop-blur sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm"
      style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
    >
      <div className="text-sm font-semibold">Enable notifications?</div>
      <div className="text-xs text-gray-400 mt-1">
        Get booking updates and important alerts. Works best when added to your home screen.
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button
          size="sm"
          onClick={registerWebPush}
          disabled={isRegisteringWeb || webPermission === 'denied'}
          className="flex-1"
        >
          {isRegisteringWeb ? 'Enabling…' : 'Enable'}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowWebPrompt(false)}
          className="flex-1"
        >
          Not now
        </Button>
      </div>
      {webPermission === 'denied' && (
        <div className="text-xs text-amber-400 mt-2">
          Notifications are blocked in your browser settings.
        </div>
      )}
    </div>
  );
};

export default PushNotificationManager;
